import { useQuery } from '@tanstack/react-query';
import { useShellSearchParams } from '../lib/use-shell-search-params';
import { useLocale } from '../i18n/locale-context';
import {
  fetchKindCounts,
  parseKindParam,
  type ProcurementKind,
  type GovSourceKey,
  parseStatusParam,
  parseIntParam,
  parseIsoDateParam,
} from '../lib/catalog-client';
import { useSearchFacets } from '../lib/use-search-facets';
import { TabStrip, type TabStripItem } from './tab-strip';

const TABS: Array<{ value: '' | ProcurementKind; labelKey: string }> = [
  { value: '', labelKey: 'catalog.kind.all' },
  { value: 'goods', labelKey: 'catalog.kind.goods' },
  { value: 'works', labelKey: 'catalog.kind.works' },
  { value: 'services', labelKey: 'catalog.kind.services' },
];

/**
 * Вкладки типа закупки: Все · Товары · Работы · Услуги. Управляют параметром
 * `kind` в URL — всё состояние в адресной строке, как у остальных фильтров.
 *
 * Счётчики обязательны, а не декоративны: пустые пересечения реальны (категория
 * «химия» + «работы» = 0 живых), и без числа пользователь жмёт вкладку, видит
 * пустой список и решает, что сайт сломан. Считаются при ТЕКУЩИХ остальных
 * фильтрах — регион/категория/ниша/статус/бюджет/дедлайн/`since` — поэтому
 * меняются вместе с ними.
 *
 * Пока активен свободный текстовый поиск (`q`), счётчики НЕ запрашиваются и
 * числа не рендерятся вовсе (вкладки остаются кликабельными, просто без
 * цифры): список при `q` ранжируется гибридным семантика+ключевые слова
 * поиском с порогом релевантности, поэтому точное число тут физически не
 * посчитать — `GET /catalog/kind-counts` тихо игнорирует `q` (считает по
 * обычному `buildWhere`), и отправка `q` только создавала врущие счётчики.
 *
 * `source` — госплощадка, которую страница УЖЕ применила к списку (чипы
 * «Площадка» в госкаталоге). Передаётся пропсом, а не читается из URL: решает,
 * действует ли `?source=`, страница, и вне госкаталога он не действует — тогда
 * и вкладки не должны его учитывать.
 */
export function KindTabs({
  scope,
  status: statusOverride,
  awarded,
  source,
}: { scope?: string; status?: string; awarded?: boolean; source?: GovSourceKey } = {}) {
  const { t } = useLocale();
  const [params, setParams] = useShellSearchParams();
  const active = parseKindParam(params.get('kind')) ?? '';

  const region = params.get('region') ?? undefined;
  const category = params.get('category') ?? undefined;
  const ikpu = params.get('ikpu') ?? undefined;
  const niche = params.get('niche') ?? undefined;
  // `/results` imposes closed+awarded by ROUTE, not by URL param, so the page
  // has to hand it down or these badges count the open catalogue instead.
  const status = statusOverride ?? parseStatusParam(params.get('status'));
  const q = params.get('q') ?? undefined;
  // Guarded exactly like `kind` above: an unrecognised value is "no filter",
  // never passed through to 400 the counts request.
  const minBudgetRaw = parseIntParam(params.get('minBudget'));
  const maxBudgetRaw = parseIntParam(params.get('maxBudget'));
  const deadlineFrom = parseIsoDateParam(params.get('deadlineFrom'));
  const deadlineTo = parseIsoDateParam(params.get('deadlineTo'));
  const since = parseIsoDateParam(params.get('since'));
  const minBudget = minBudgetRaw !== undefined ? Number(minBudgetRaw) : undefined;
  const maxBudget = maxBudgetRaw !== undefined ? Number(maxBudgetRaw) : undefined;

  // While a query is active the plain kind-counts endpoint would lie (it
  // ignores `q`), so it's disabled below; the counts instead come from the
  // search facets (`useSearchFacets`), which count over the SAME hybrid match
  // set the results do. Not on `/results` (awarded archive), which the search
  // facets don't model — there the tabs stay uncounted, as before.
  const hasQuery = Boolean(q);
  const useFacetCounts = hasQuery && !awarded;
  const { facets } = useSearchFacets();

  const { data: counts } = useQuery({
    queryKey: [
      'catalog',
      'kind-counts',
      {
        region,
        category,
        ikpu,
        niche,
        status,
        scope,
        awarded,
        minBudget,
        maxBudget,
        deadlineFrom,
        deadlineTo,
        since,
        source,
      },
    ],
    queryFn: () =>
      fetchKindCounts({
        region,
        category,
        ikpu,
        niche,
        status,
        scope,
        awarded,
        minBudget,
        maxBudget,
        deadlineFrom,
        deadlineTo,
        since,
        source,
      }),
    enabled: !hasQuery,
  });

  function select(value: '' | ProcurementKind) {
    const next = new URLSearchParams(params);
    if (value) next.set('kind', value);
    else next.delete('kind');
    next.delete('page'); // смена вкладки сбрасывает пагинацию
    setParams(next);
  }

  function countFor(value: '' | ProcurementKind): number | undefined {
    // During a search, the per-kind numbers come from the search facets (the
    // `kind.all` there is the grand total, kind-less lots included — the same
    // number the «Все» tab wants).
    if (useFacetCounts) {
      const k = facets?.kind;
      if (!k) return undefined;
      return value === '' ? k.all : k[value];
    }
    if (!counts) return undefined;
    return value === '' ? counts.all : counts[value];
  }

  const items: TabStripItem<'' | ProcurementKind>[] = TABS.map((tab) => {
    const count = countFor(tab.value);
    const label = t(tab.labelKey);
    return {
      value: tab.value,
      label,
      count,
      // Пока счётчики грузятся — просто метка, без фразы с пустым числом.
      ariaLabel: count != null ? t('catalog.kind.ariaLabel', { label, count }) : label,
    };
  });

  return (
    <TabStrip ariaLabel={t('catalog.kind.aria')} items={items} active={active} onSelect={select} />
  );
}
